import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Paperclip, X } from "lucide-react";
import { CATEGORIES } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-[#FBF9F6]/20 text-[#FBF9F6] placeholder-[#FBF9F6]/40 px-4 py-3.5 text-sm font-light focus:outline-none focus:border-[#BFA26F] transition-colors duration-200 rounded-none";

const empty = {
  name: "",
  company: "",
  email: "",
  country: "",
  website: "",
  category: "",
  export_markets: "",
  capacity: "",
  message: "",
};

export const EnquiryForm = () => {
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await axios.post(`${API}/enquiries`, fd);
      toast.success("Thank you. Your submission has been received by our procurement team.");
      setForm(empty);
      setFile(null);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="enquiry-form"
      className="border border-[#FBF9F6]/15 p-8 md:p-12 bg-[#FBF9F6]/[0.02]"
    >
      <h3 className="font-display text-2xl md:text-3xl font-medium text-[#FBF9F6] mb-2">
        Contact International Procurement
      </h3>
      <p className="text-sm font-light text-[#FBF9F6]/60 mb-10">
        Tell us about your company and products.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input required data-testid="enquiry-name-input" placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
        <input required data-testid="enquiry-company-input" placeholder="Company *" value={form.company} onChange={set("company")} className={inputCls} />
        <input required type="email" data-testid="enquiry-email-input" placeholder="Business Email *" value={form.email} onChange={set("email")} className={inputCls} />
        <input required data-testid="enquiry-country-input" placeholder="Country *" value={form.country} onChange={set("country")} className={inputCls} />
        <input data-testid="enquiry-website-input" placeholder="Website" value={form.website} onChange={set("website")} className={inputCls} />
        <select
          required
          data-testid="enquiry-category-select"
          value={form.category}
          onChange={set("category")}
          className={`${inputCls} appearance-none ${form.category ? "" : "text-[#FBF9F6]/40"}`}
        >
          <option value="" disabled className="text-[#111413]">Product Category *</option>
          {[...CATEGORIES.map((c) => c.title), "Other"].map((c) => (
            <option key={c} value={c} className="text-[#111413]">{c}</option>
          ))}
        </select>
        <input data-testid="enquiry-export-markets-input" placeholder="Current Export Markets" value={form.export_markets} onChange={set("export_markets")} className={inputCls} />
        <input data-testid="enquiry-capacity-input" placeholder="Manufacturing / Production Capacity" value={form.capacity} onChange={set("capacity")} className={inputCls} />
        <textarea
          required
          rows={5}
          data-testid="enquiry-message-textarea"
          placeholder="Tell us about your products, production capacity and export experience *"
          value={form.message}
          onChange={set("message")}
          className={`${inputCls} sm:col-span-2 resize-none`}
        />
        <div className="sm:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#FBF9F6]/50 mb-3">
            Company Profile / Catalogue
          </p>
          <input
            ref={fileRef}
            type="file"
            data-testid="enquiry-file-input"
            className="hidden"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.zip"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file ? (
            <div className="flex items-center justify-between border border-[#BFA26F]/50 px-4 py-3.5" data-testid="enquiry-file-selected">
              <span className="text-sm font-light text-[#FBF9F6]/85 flex items-center gap-3 truncate">
                <Paperclip size={15} strokeWidth={1.5} className="text-[#BFA26F] shrink-0" />
                {file.name}
              </span>
              <button
                type="button"
                data-testid="enquiry-file-remove"
                onClick={() => {
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                className="text-[#FBF9F6]/60 hover:text-[#BFA26F] transition-colors duration-200"
                aria-label="Remove file"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="enquiry-file-upload-button"
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-[#FBF9F6]/25 px-4 py-5 text-sm font-light text-[#FBF9F6]/60 hover:border-[#BFA26F] hover:text-[#BFA26F] transition-[border-color,color] duration-200 flex items-center justify-center gap-3"
            >
              <Paperclip size={15} strokeWidth={1.5} /> Attach Company Profile / Catalogue
            </button>
          )}
        </div>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="enquiry-form-submit"
        className="group mt-8 inline-flex items-center gap-3 px-10 py-4 bg-[#BFA26F] text-[#0A1F16] text-sm font-bold tracking-widest uppercase hover:bg-[#FBF9F6] transition-colors duration-300 disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : "Submit to Procurement"}
        {!sending && (
          <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
        )}
      </button>
    </form>
  );
};
